import { withPlugins, inview } from '@spon/plugins';

/*
 *		Fraction of the viewport the observer's root box is shrunk by at the
 *		bottom. Kept as a number so the rootMargin string and the
 *		unreachable-element maths below can never drift apart.
 */
const BOTTOM_MARGIN = 0.1;
const RESIZE_DEBOUNCE = 150;

function FadeIn({ node, plugins: { inview } }) {
	/*
	 *		Variables
	 */

	const items = [...node.querySelectorAll('[data-fade-in]')];

	inview.settings = {
		rootMargin: `0px 0px -${BOTTOM_MARGIN * 100}% 0px`,
	};

	// watch some other nodes
	inview.observe(items, {
		enter: ({ target }) => {
			if (!target.classList.contains('has-moved')) {
				target.classList.add('has-moved'); // handled with CSS.
			}
		},
	});

	/*
	 *		Two ways an element can be stranded at opacity 0 with no scroll
	 *		that will ever rescue it. Both are handled here, in one read pass.
	 *
	 *		ONE — ON SCREEN AT FIRST PAINT, BELOW THE REVEAL LINE.
	 *		The observer's root is shrunk by BOTTOM_MARGIN, so the reveal line
	 *		sits at 90% of the viewport, not at its foot. Anything between
	 *		those two points is visible to the visitor and invisible to the
	 *		observer: the page opens with a hole in it, and only scrolling
	 *		fills it. That is not a scroll reveal, it is missing content.
	 *
	 *		Measured on /contact at 1440x900 during Task B.1: the page-header
	 *		intro sat at top 562 against a (then) 540 line and rendered blank,
	 *		as did the form and the aside below it. The rule is simply that if you
	 *		can see it when the page opens, it should be there — so anything
	 *		intersecting the REAL viewport is revealed, and the shrunk root
	 *		goes on governing everything genuinely below the fold, which is
	 *		what it is for.
	 *
	 *		TWO — PARKED IN THE UNREACHABLE DEAD ZONE AT THE PAGE FOOT.
	 *		An element only ever crosses the line if its document-top is less
	 *		than maxScroll + line — i.e. less than
	 *		scrollHeight - BOTTOM_MARGIN * viewportHeight. Anything at or below
	 *		that is in a dead zone: at maximum scroll it is STILL under the
	 *		line, so the observer never fires and [data-fade-in]'s opacity-0
	 *		never lifts. The content is invisible for the life of the page.
	 *
	 *		Measured on /notes at 1440x1400 before that fix: the last note card
	 *		sat permanently at opacity 0. The taller the viewport, the bigger
	 *		the dead zone, so it bites hardest on desktop.
	 *
	 *		Reveal both directly. Re-measured on layout and viewport changes
	 *		because both zones move with both. Not per-frame work — mount plus
	 *		debounced resize only — so the rect reads here are cheap.
	 *
	 *		Two passes, not one: reading every rect first and writing every
	 *		class afterwards keeps the interleaved read/write out of the loop,
	 *		so the browser lays out once instead of once per revealed item
	 *		(house rule on caching getBoundingClientRect reads).
	 *
	 *		scrollHeight is read from documentElement while the observer below
	 *		watches document.body. Equivalent here: body is the sole flow root
	 *		and neither element is independently scrollable or height-capped,
	 *		so body's box grows exactly with the document's scrollable height.
	 */
	const revealStranded = () => {
		const viewportHeight = window.innerHeight;
		const deadZoneTop =
			document.documentElement.scrollHeight - viewportHeight * BOTTOM_MARGIN;

		const stranded = items.filter(item => {
			if (item.classList.contains('has-moved')) return false;

			const rect = item.getBoundingClientRect();

			// One — intersecting the real viewport, so the visitor can see it
			if (rect.top < viewportHeight && rect.bottom > 0) return true;

			// Two — parked below the last position the reveal line can reach
			return rect.top + window.scrollY >= deadZoneTop;
		});

		stranded.forEach(item => item.classList.add('has-moved'));
	};

	// Document height changes (images, fonts, late content) move both zones.
	// ResizeObserver is already batched by the browser — do not debounce it.
	// Its first callback fires on observe() with the element's current size,
	// which is the mount-time pass — no separate initial call is needed, and
	// it is that first pass which clears the first-paint hole.
	const documentObserver = new ResizeObserver(revealStranded);
	documentObserver.observe(document.body);

	// Viewport height changes move them too, and need not change body's size.
	let resizeTimer = null;
	const onResize = () => {
		window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(revealStranded, RESIZE_DEBOUNCE);
	};

	window.addEventListener('resize', onResize);

	return () => {
		window.clearTimeout(resizeTimer);
		window.removeEventListener('resize', onResize);
		documentObserver.disconnect();
	};
}
export default withPlugins(inview)(FadeIn);
